import { Fingerprint } from "lucide-react";
import * as React from "react";

import type { FilterDefinition } from "@/shared/foundation/primitives/Table";

/**
 * Comparators for resource-id flavored string fields — opaque identifiers
 * the backend matches exactly. Mirrors `RESOURCE_ID_FIELD_OPERATORS` in
 * `packages/roboql/src/roboql/model/core.py`, which the roboQL schema
 * validator enforces against `id`, `dataset.dataset_id`, and
 * `device.device_id` before the predicate ever reaches
 * `SessionPredicateGenerator`. Keep this allow-list in sync with the
 * backend constant.
 */
const RESOURCE_ID_COMPARATORS = ["EQUALS", "NOT_EQUALS"] as const;

/**
 * Comparators for tag-array fields. Mirrors `TAG_FIELD_COMPARATORS` in
 * `packages/roboql/src/roboql/model/core.py`, which the roboQL schema
 * validator enforces against `tags` (and other tag-flavored fields)
 * before the predicate ever reaches `SessionPredicateGenerator`. Keep
 * this allow-list in sync with the backend constant.
 */
const TAG_FIELD_COMPARATORS = ["CONTAINS", "NOT_CONTAINS"] as const;

/** Fingerprint glyph shared by every resource-id flavored Sessions filter row. */
const RESOURCE_ID_ICON = React.createElement(Fingerprint);

/**
 * Comparator + icon preset that mounts apply to the cross-aggregate id
 * rows (`dataset.dataset_id`, `device.device_id`) they append, so those
 * rows match the core `Session ID` row's operator set and glyph.
 */
export const sessionResourceIdFilter = {
  comparators: RESOURCE_ID_COMPARATORS,
  icon: RESOURCE_ID_ICON,
} as const;

/**
 * Filterable Sessions fields common to every Sessions list mount. Mounts
 * compose their picker by spreading this array and appending the
 * cross-aggregate id rows their scope admits. Each `field` is the roboQL
 * token (alias or canonical label) the backend route forwards to
 * `SessionPredicateGenerator`.
 */
export const coreSessionFilters = [
  {
    type: "string",
    field: "id",
    name: "Session ID",
    allowedComparators: RESOURCE_ID_COMPARATORS,
    icon: RESOURCE_ID_ICON,
  },
  { type: "string", field: "name", name: "Name" },
  { type: "date", field: "start_time", name: "Start time" },
  { type: "date", field: "end_time", name: "End time" },
  { type: "numeric", field: "duration", name: "Duration (ns)" },
  // Free-text input — no `options` resolver because the backend does not
  // expose a Sessions tag autocomplete endpoint. Datasets/Events/Files
  // populate their tag picker via `getTagsForOrg`; until Sessions ships
  // the equivalent, users type tag values directly.
  {
    type: "string",
    field: "tags",
    name: "Tags",
    allowedComparators: TAG_FIELD_COMPARATORS,
  },
] as const satisfies readonly FilterDefinition[];
